import React, { useEffect, useState } from 'react';
import { IconButton } from '@material-ui/core';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import styles from '../constants/styles';
import {
  getAllEntitiesCatalog as getEntitiesCatalog,
  getEntities as getManufacturers,
  setPageAction as setPage,
  setRowsPerPageAction as setRowsPerPage,
} from '../../../redux/actions/manufacturersActions';
import EditIcon from '@material-ui/icons/Edit';
import AddBoxIcon from '@material-ui/icons/AddBox';
import TableView from '../../TableView/TableView';
import ColumnInterface from '../../../interfaces/column';
import RBISearchButton from '../../RBISearchButton/RBISearchButton';
import RBISelectButton from '../../RBISelectButton/RBISelectButton';
import ManufacturerCreate from './ManufacturerCreate';
import ManufacturerEdit from './ManufacturerEdit';
import { actionListener, cleanEmptyString } from '../../../utils/helpers';
import store from '../../../redux/store';
import {
  MANUFACTURERS_CREATE_ENTITY,
  MANUFACTURERS_DELETE_ENTITY,
  MANUFACTURERS_EDIT_ENTITY,
} from '../../../redux/action-types/manufacturers-types';
import { useTranslation } from 'react-i18next';
import AppConstants from '../../../AppConstants';

export default function ManufacturersTab() {
  const classes = styles();
  const dispatch: any = useDispatch();
  const selectedBrand = useSelector(
    (state: RootStateOrAny) => state.UIReducer.selectedBrand,
  );
  const manufacturers = useSelector(
    (state: RootStateOrAny) => state.ManufacturersReducer.entities,
  );
  const manufacturersCatalog = useSelector(
    (state: RootStateOrAny) => state.ManufacturersReducer.entitiesCatalog,
  );
  const paginationConfig = useSelector(
    (state: RootStateOrAny) => state.ManufacturersReducer.paginationConfig,
  );
  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [treatedManufacturer, setTreatedManufacturer] = React.useState<any[]>(
    [],
  );
  const [manufacturer, setManufacturer] = useState({});

  const handleModalManufacturerEdit =
    (value, idCategory = 0) =>
    () => {
      if (idCategory) {
        setManufacturer(manufacturers.find(c => c.id === idCategory));
      }
      setOpenEdit(value);
    };

  const { t } = useTranslation();

  const manufacturerStrings = {
    id: t('app.common.id'),
    status: t('app.common.status'),
    manufacturer_name: t('createManufacturer.manufacturerName'),
    active: t('app.common.active'),
    inactive: t('app.common.inactive'),
    not_manufacturer_found: t('createManufacturer.noManufacturerFound'),
    create_manufacturer: t('createManufacturer.createManufacturer'),
    edit_manufacturer: t('createManufacturer.editManufacturer'),
    new_manufacturer: t('createManufacturer.newManufacturer'),
    manufacturer: t('createManufacturer.manufacturer'),
    cancel: t('app.common.cancel'),
    add_to_catalog: t('createManufacturer.addToCatalog'),
    activate: t('createManufacturer.activate'),
    inactivate: t('createManufacturer.inactivate'),
    save: t('app.common.save'),
  };

  useEffect(() => {
    setTreatedManufacturer(
      manufacturers.map(m => {
        const newManufacturer = { ...m };
        newManufacturer.action = (
          <IconButton
            onClick={handleModalManufacturerEdit(true, newManufacturer.id)}
          >
            <EditIcon />
          </IconButton>
        );
        newManufacturer.status = m.status
          ? manufacturerStrings.active
          : manufacturerStrings.inactive;
        return newManufacturer;
      }),
    );
  }, [manufacturers]);

  const [searchObject, setSearchObject] = useState<any>({
    status: '',
    name: '',
  });
  const [searchObjectPrev, setSearchObjectPrev] = useState<any>({
    status: '',
    name: '',
  });

  const refreshOnSave = () => {
    dispatch(
      getEntitiesCatalog({
        brand: selectedBrand,
      }),
    );

    dispatch(
      getManufacturers({
        ...cleanEmptyString(searchObject),
        brand: selectedBrand,
        page: paginationConfig.page,
        limit: paginationConfig.rowsPerPage,
        paginated: 1,
      }),
    );
  };

  useEffect(() => {
    const unsubscribeCategoryEdit = actionListener(
      store,
      MANUFACTURERS_EDIT_ENTITY.SUCCESS,
      function (action) {
        refreshOnSave();
      },
    );
    const unsubscribeCategoryCreate = actionListener(
      store,
      MANUFACTURERS_CREATE_ENTITY.SUCCESS,
      function (action) {
        refreshOnSave();
      },
    );
    const unsubscribeManufacturerDelete = actionListener(
      store,
      MANUFACTURERS_DELETE_ENTITY.SUCCESS,
      function (action) {
        refreshOnSave();
      },
    );
    return function () {
      unsubscribeCategoryEdit();
      unsubscribeCategoryCreate();
      unsubscribeManufacturerDelete();
    };
  }, [
    paginationConfig.page,
    paginationConfig.rowsPerPage,
    searchObject.status,
  ]);
  useEffect(() => {
    if (selectedBrand) {
      dispatch(setPage(1));
      dispatch(setRowsPerPage(AppConstants.rowsPerPage));
      dispatch(
        getManufacturers({
          brand: selectedBrand,
          page: 1,
          limit: AppConstants.rowsPerPage,
          paginated: 1,
        }),
      );
      dispatch(
        getEntitiesCatalog({
          brand: selectedBrand,
          page: 1,
          limit: AppConstants.rowsPerPage,
          paginated: 1,
        }),
      );
    }
  }, [selectedBrand]);

  const handleApplyButton = () => {
    setSearchObject({ ...searchObjectPrev });
    dispatch(setPage(1));
    dispatch(
      getManufacturers({
        page: 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        ...cleanEmptyString(searchObjectPrev),
        name: searchObjectPrev.name.name,
        paginated: 1,
      }),
    );
  };

  const statusOptions = [
    { name: t('app.common.All'), value: '' },
    { name: t('app.common.active'), value: 1 },
    { name: t('app.common.inactive'), value: 0 },
  ];

  const handleModalCreate = value => () => {
    setOpenCreate(value);
  };

  const handleChangeAutocomplete = (name: string, value: any) => {
    setSearchObjectPrev({
      ...searchObject,
      [name]: value ? value : '',
    });
    if (!value) {
      setSearchObject({
        ...searchObject,
        [name]: value ? value : '',
      });
    }
  };

  const handleInputChangeAutocomplete = (name: string, value: any) => {};

  const handleChangeSelect = (
    event: React.ChangeEvent<{ name?: string; value: unknown }>,
  ) => {
    const name = event.target.name as keyof typeof searchObject;
    setSearchObjectPrev({
      ...searchObject,
      [name]: event.target.value,
    });
  };

  const columns: ColumnInterface[] = [
    {
      id: 'id',
      label: manufacturerStrings.id,
      align: 'left',
    },
    {
      id: 'name',
      label: (
        <RBISearchButton
          id={'name'}
          freeSolo={true}
          valueOption={searchObjectPrev['name']}
          labelText={manufacturerStrings.manufacturer_name}
          onChange={handleChangeAutocomplete}
          handleApplyButton={handleApplyButton}
          options={manufacturersCatalog}
        />
      ),
      align: 'left',
    },
    {
      id: 'status',
      label: (
        <RBISelectButton
          id={'status'}
          valueOption={searchObjectPrev['status']}
          labelText={manufacturerStrings.status}
          onChangeInput={handleChangeSelect}
          width={100}
          options={statusOptions}
          handleApplyButton={handleApplyButton}
        />
      ),
      align: 'left',
    },
    {
      id: 'action',
      label: (
        <IconButton onClick={handleModalCreate(true)}>
          <AddBoxIcon />
        </IconButton>
      ),
    },
  ];

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    dispatch(setRowsPerPage(+event.target.value));
    dispatch(setPage(1));
    dispatch(
      getManufacturers({
        ...cleanEmptyString(searchObject),
        page: 1,
        limit: +event.target.value,
        brand: selectedBrand,
        paginated: 1,
      }),
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    dispatch(setPage(newPage + 1));
    dispatch(
      getManufacturers({
        ...cleanEmptyString(searchObject),
        page: newPage + 1,
        limit: paginationConfig.rowsPerPage,
        brand: selectedBrand,
        paginated: 1,
      }),
    );
  };

  return (
    <div className={classes.root}>
      <div>
        {openCreate && (
          <ManufacturerCreate
            open={openCreate}
            handleClose={handleModalCreate(false)}
          />
        )}
        {openEdit && (
          <ManufacturerEdit
            open={openEdit}
            handleClose={handleModalManufacturerEdit(false)}
            manufacturerToEdit={manufacturer}
          />
        )}
      </div>
      <div>
        <TableView
          columns={columns}
          rows={treatedManufacturer}
          paginationConfig={paginationConfig}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          handleChangePage={handleChangePage}
          messageEmptyArray={manufacturerStrings.not_manufacturer_found}
        />
      </div>
    </div>
  );
}
